import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    FileText,
    Save,
    RefreshCw,
    ShieldAlert,
    CheckCircle2,
    AlertTriangle,
    Eye,
    Pencil,
    Bold,
    Italic,
    Underline,
    List,
    ListOrdered,
    AlignLeft,
    AlignCenter,
    AlignRight,
    Link as LinkIcon,
    Table2,
    Heading1,
    Heading2,
    Heading3,
    Eraser,
} from "lucide-react";

export default function ManualeHaccpTemplateAdmin({ isSuper = false }) {
    const editorRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [html, setHtml] = useState("");
    const [originalHtml, setOriginalHtml] = useState("");
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [viewMode, setViewMode] = useState("editor"); // editor | preview | split

    const dirty = useMemo(() => html !== originalHtml, [html, originalHtml]);

    useEffect(() => {
        loadTemplate();
    }, []);

    useEffect(() => {
        if (!loading && editorRef.current && editorRef.current.innerHTML !== html) {
            editorRef.current.innerHTML = html || "";
        }
    }, [loading, html]);

    async function loadTemplate() {
        setLoading(true);
        setError("");
        setMessage("");

        try {
            const res = await fetch("/api/admin/manuale_haccp_template_get.php", {
                credentials: "include",
            });

            const data = await res.json().catch(() => null);

            if (!res.ok || !data?.ok) {
                throw new Error(data?.error || "Impossibile caricare il template.");
            }

            const content = String(data.html || "");
            setHtml(content);
            setOriginalHtml(content);
        } catch (err) {
            setError(err.message || "Errore di caricamento.");
        } finally {
            setLoading(false);
        }
    }

    async function saveTemplate() {
        if (!isSuper) return;

        setSaving(true);
        setError("");
        setMessage("");

        try {
            const currentHtml = editorRef.current?.innerHTML ?? html;

            const res = await fetch("/api/admin/manuale_haccp_template_save.php", {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    html: currentHtml,
                }),
            });

            const data = await res.json().catch(() => null);

            if (!res.ok || !data?.ok) {
                throw new Error(data?.error || "Salvataggio non riuscito.");
            }

            setHtml(currentHtml);
            setOriginalHtml(currentHtml);
            setMessage("Template salvato correttamente.");
        } catch (err) {
            setError(err.message || "Errore durante il salvataggio.");
        } finally {
            setSaving(false);
        }
    }

    function resetChanges() {
        if (editorRef.current) {
            editorRef.current.innerHTML = originalHtml || "";
        }
        setHtml(originalHtml || "");
        setMessage("");
        setError("");
    }

    function syncEditor() {
        const value = editorRef.current?.innerHTML ?? "";
        setHtml(value);
    }

    function exec(command, value = null) {
        editorRef.current?.focus();
        document.execCommand(command, false, value);
        syncEditor();
    }

    function formatBlock(tag) {
        editorRef.current?.focus();
        document.execCommand("formatBlock", false, tag);
        syncEditor();
    }

    function insertLink() {
        const url = window.prompt("Inserisci URL del link:");
        if (!url) return;
        exec("createLink", url);
    }

    function removeFormatting() {
        exec("removeFormat");
    }

    function insertTable() {
        editorRef.current?.focus();
        const tableHtml = `
      <table style="width:100%; border-collapse:collapse; margin:12px 0;">
        <tbody>
          <tr>
            <td style="border:1px solid #cbd5e1; padding:8px;">Cella 1</td>
            <td style="border:1px solid #cbd5e1; padding:8px;">Cella 2</td>
          </tr>
          <tr>
            <td style="border:1px solid #cbd5e1; padding:8px;">Cella 3</td>
            <td style="border:1px solid #cbd5e1; padding:8px;">Cella 4</td>
          </tr>
        </tbody>
      </table>
    `;
        document.execCommand("insertHTML", false, tableHtml);
        syncEditor();
    }

    if (!isSuper) {
        return (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-start gap-3">
                    <div className="h-11 w-11 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                        <ShieldAlert size={22} />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-slate-900">
                            Template Manuale HACCP
                        </h2>
                        <p className="mt-2 text-slate-600">
                            Questa sezione è riservata esclusivamente al super admin.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const previewDoc = `
    <!doctype html>
    <html lang="it">
      <head>
        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>Anteprima Manuale HACCP</title>
        <style>
          body {
            font-family: Arial, Helvetica, sans-serif;
            margin: 24px;
            color: #0f172a;
            background: #fff;
            line-height: 1.5;
          }
          img { max-width: 100%; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #cbd5e1; padding: 8px; vertical-align: top; }
          h1, h2, h3 { margin-top: 1em; margin-bottom: 0.5em; }
          p { margin: 0 0 0.75em; }
          ul, ol { margin: 0 0 0.75em 1.25em; }
        </style>
      </head>
      <body>
        ${html || "<p style='color:#64748b'>Nessun contenuto disponibile.</p>"}
      </body>
    </html>
  `;

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-900 via-indigo-800 to-sky-600 text-white">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex items-start gap-3">
                            <div className="h-11 w-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                                <FileText size={22} />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold">Editor Visuale Manuale HACCP</h2>
                                <p className="text-sm text-white/85 mt-1">
                                    Modifica il template con editor visuale, senza toccare il codice HTML.
                                </p>
                                <p className="text-xs text-white/75 mt-1 break-all">
                                    File: /var/www/html/dashboard-haccp/file/templates/manuale_haccp_rev02.html
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-2 flex-wrap">
                            <button
                                type="button"
                                onClick={loadTemplate}
                                disabled={loading || saving}
                                className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 disabled:opacity-50 text-sm flex items-center gap-2"
                            >
                                <RefreshCw size={16} />
                                Ricarica
                            </button>

                            <button
                                type="button"
                                onClick={resetChanges}
                                disabled={!dirty || saving || loading}
                                className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 disabled:opacity-50 text-sm flex items-center gap-2"
                            >
                                <RefreshCw size={16} />
                                Annulla modifiche
                            </button>

                            <button
                                type="button"
                                onClick={saveTemplate}
                                disabled={!dirty || saving || loading}
                                className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-sm font-medium flex items-center gap-2"
                            >
                                <Save size={16} />
                                {saving ? "Salvataggio..." : "Salva"}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
                    <div className="flex gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={() => setViewMode("editor")}
                            className={
                                viewMode === "editor"
                                    ? "px-4 py-2 rounded-full bg-slate-900 text-white text-sm flex items-center gap-2"
                                    : "px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-sm flex items-center gap-2"
                            }
                        >
                            <Pencil size={15} />
                            Solo editor
                        </button>

                        <button
                            type="button"
                            onClick={() => setViewMode("preview")}
                            className={
                                viewMode === "preview"
                                    ? "px-4 py-2 rounded-full bg-slate-900 text-white text-sm flex items-center gap-2"
                                    : "px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-sm flex items-center gap-2"
                            }
                        >
                            <Eye size={15} />
                            Solo anteprima
                        </button>

                        <button
                            type="button"
                            onClick={() => setViewMode("split")}
                            className={
                                viewMode === "split"
                                    ? "px-4 py-2 rounded-full bg-slate-900 text-white text-sm flex items-center gap-2"
                                    : "px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-sm flex items-center gap-2"
                            }
                        >
                            <FileText size={15} />
                            Editor + Anteprima
                        </button>
                    </div>

                    {message && (
                        <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-800 text-sm flex items-start gap-2">
                            <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
                            <span>{message}</span>
                        </div>
                    )}

                    {error && (
                        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-800 text-sm flex items-start gap-2">
                            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="p-6 text-slate-600">Caricamento editor in corso...</div>
                ) : (
                    <div
                        className={
                            viewMode === "split"
                                ? "grid grid-cols-1 xl:grid-cols-2"
                                : "grid grid-cols-1"
                        }
                    >
                        {(viewMode === "editor" || viewMode === "split") && (
                            <div className={viewMode === "split" ? "border-r border-slate-200" : ""}>
                                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 text-sm font-semibold text-slate-700">
                                    Editor visuale
                                </div>

                                <div className="p-3 border-b border-slate-200 bg-white flex flex-wrap gap-2">
                                    <ToolbarButton onClick={() => formatBlock("<h1>")} title="Titolo 1">
                                        <Heading1 size={16} />
                                    </ToolbarButton>

                                    <ToolbarButton onClick={() => formatBlock("<h2>")} title="Titolo 2">
                                        <Heading2 size={16} />
                                    </ToolbarButton>

                                    <ToolbarButton onClick={() => formatBlock("<h3>")} title="Titolo 3">
                                        <Heading3 size={16} />
                                    </ToolbarButton>

                                    <ToolbarButton onClick={() => formatBlock("<p>")} title="Paragrafo">
                                        P
                                    </ToolbarButton>

                                    <div className="w-px h-8 bg-slate-200 mx-1" />

                                    <ToolbarButton onClick={() => exec("bold")} title="Grassetto">
                                        <Bold size={16} />
                                    </ToolbarButton>

                                    <ToolbarButton onClick={() => exec("italic")} title="Corsivo">
                                        <Italic size={16} />
                                    </ToolbarButton>

                                    <ToolbarButton onClick={() => exec("underline")} title="Sottolineato">
                                        <Underline size={16} />
                                    </ToolbarButton>

                                    <ToolbarButton onClick={removeFormatting} title="Rimuovi formattazione">
                                        <Eraser size={16} />
                                    </ToolbarButton>

                                    <div className="w-px h-8 bg-slate-200 mx-1" />

                                    <ToolbarButton onClick={() => exec("insertUnorderedList")} title="Elenco puntato">
                                        <List size={16} />
                                    </ToolbarButton>

                                    <ToolbarButton onClick={() => exec("insertOrderedList")} title="Elenco numerato">
                                        <ListOrdered size={16} />
                                    </ToolbarButton>

                                    <div className="w-px h-8 bg-slate-200 mx-1" />

                                    <ToolbarButton onClick={() => exec("justifyLeft")} title="Allinea a sinistra">
                                        <AlignLeft size={16} />
                                    </ToolbarButton>

                                    <ToolbarButton onClick={() => exec("justifyCenter")} title="Allinea al centro">
                                        <AlignCenter size={16} />
                                    </ToolbarButton>

                                    <ToolbarButton onClick={() => exec("justifyRight")} title="Allinea a destra">
                                        <AlignRight size={16} />
                                    </ToolbarButton>

                                    <div className="w-px h-8 bg-slate-200 mx-1" />

                                    <ToolbarButton onClick={insertLink} title="Inserisci link">
                                        <LinkIcon size={16} />
                                    </ToolbarButton>

                                    <ToolbarButton onClick={insertTable} title="Inserisci tabella">
                                        <Table2 size={16} />
                                    </ToolbarButton>
                                </div>

                                <div className="p-4 bg-slate-100 min-h-[70vh]">
                                    <div className="max-w-[900px] mx-auto bg-white shadow-sm rounded-xl border border-slate-200 min-h-[65vh] overflow-hidden">
                                        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 text-xs text-slate-500">
                                            Documento modificabile
                                        </div>

                                        <div
                                            ref={editorRef}
                                            contentEditable
                                            suppressContentEditableWarning
                                            onInput={syncEditor}
                                            onBlur={syncEditor}
                                            className="min-h-[60vh] p-6 outline-none text-slate-800 leading-7"
                                            style={{
                                                fontFamily: "Arial, Helvetica, sans-serif",
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {(viewMode === "preview" || viewMode === "split") && (
                            <div>
                                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 text-sm font-semibold text-slate-700">
                                    Anteprima documento
                                </div>
                                <iframe
                                    title="Anteprima manuale HACCP"
                                    srcDoc={previewDoc}
                                    className="w-full min-h-[70vh] bg-white"
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function ToolbarButton({ children, onClick, title }) {
    return (
        <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClick}
            title={title}
            className="h-9 min-w-9 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 flex items-center justify-center gap-2 text-sm"
        >
            {children}
        </button>
    );
}